import { useState } from "react";
import { Building2, Phone, Mail, Globe, Facebook, Instagram, Twitter, ExternalLink } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";

export default function PublicSponsors() {
  const { data, loading } = usePublicData();
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  const teams = data?.teams || [];
  const sponsors = data?.sponsors || [];

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.name || "Unknown";
  };

  const getTeamLogo = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.logo;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Our Sponsors
        </h1>
        <p className="text-muted-foreground mt-1">
          Meet the businesses supporting our tournament teams
        </p>
      </div>

      {/* Sponsors Grid */}
      {sponsors.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-6">
          {sponsors.map((sponsor, index) => (
            <div
              key={sponsor.id}
              className="bg-card border border-border rounded-xl overflow-hidden animate-fade-in opacity-0"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Team Header with Logo */}
              <div className="p-4 bg-gradient-to-r from-primary/20 to-transparent flex items-center gap-4">
                {getTeamLogo(sponsor.team_id) ? (
                  <img
                    src={getTeamLogo(sponsor.team_id)}
                    alt=""
                    className="w-16 h-16 rounded-xl object-cover border-2 border-border"
                  />
                ) : (
                  <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center border-2 border-border">
                    <Building2 className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">
                    Proud Sponsor of
                  </p>
                  <p className="font-heading text-xl font-bold uppercase">
                    {getTeamName(sponsor.team_id)}
                  </p>
                </div>
              </div>

              {/* Sponsor Content */}
              <div className="p-5 space-y-4">
                {/* Business Name */}
                <h2 className="font-heading text-2xl font-black uppercase text-primary">
                  {sponsor.business_name}
                </h2>

                {/* Description */}
                <p className="text-muted-foreground leading-relaxed">
                  {sponsor.description}
                </p>

                {/* Images Gallery */}
                {sponsor.images?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {sponsor.images.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt=""
                        className="w-full aspect-square rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setSelectedSponsor({ ...sponsor, selectedImage: img })}
                      />
                    ))}
                  </div>
                )}

                {/* Contact Information */}
                <div className="pt-4 border-t border-border space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Contact Information
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {sponsor.contact_phone && (
                      <a
                        href={`tel:${sponsor.contact_phone}`}
                        className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                      >
                        <Phone className="w-5 h-5 text-primary" />
                        <span className="text-sm">{sponsor.contact_phone}</span>
                      </a>
                    )}
                    {sponsor.contact_email && (
                      <a
                        href={`mailto:${sponsor.contact_email}`}
                        className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                      >
                        <Mail className="w-5 h-5 text-primary" />
                        <span className="text-sm truncate">{sponsor.contact_email}</span>
                      </a>
                    )}
                  </div>

                  {sponsor.website && (
                    <a
                      href={sponsor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-primary" />
                        <span className="text-sm font-medium">Visit Website</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </a>
                  )}
                </div>

                {/* Social Media */}
                {(sponsor.facebook || sponsor.instagram || sponsor.twitter) && (
                  <div className="pt-4 border-t border-border">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                      Follow Us
                    </h3>
                    <div className="flex gap-3">
                      {sponsor.facebook && (
                        <a
                          href={sponsor.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                        >
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                      {sponsor.instagram && (
                        <a
                          href={sponsor.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 bg-pink-600/20 text-pink-400 rounded-lg hover:bg-pink-600/30 transition-colors"
                        >
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {sponsor.twitter && (
                        <a
                          href={sponsor.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 bg-sky-500/20 text-sky-400 rounded-lg hover:bg-sky-500/30 transition-colors"
                        >
                          <Twitter className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Building2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            Sponsor Information Coming Soon
          </h3>
          <p className="text-muted-foreground">
            Details about our team sponsors will be available shortly.
          </p>
        </div>
      )}

      {/* Image Lightbox */}
      {selectedSponsor && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedSponsor(null)}
        >
          <img
            src={selectedSponsor.selectedImage}
            alt=""
            className="max-w-full max-h-[90vh] rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
